import React, { useState, useEffect } from 'react';
import { Container, Image, Row, Col, Form, Button, Alert, Modal } from 'react-bootstrap';
import { PasswordRequirementsBox, buildPasswordSchema, checkPasswordRequirements } from './Utils';
import RestAuthManager from '.';
import Cookies from 'js-cookie';
import { parseJwt } from './Utils';

const API = RestAuthManager.API;

// Neutral forgot-password response. Shown verbatim regardless of whether the address
// exists (and on network errors) so the form never leaks account existence.
const NEUTRAL_RESET_MESSAGE = 'If an account exists for that address, a reset link has been sent.';

const Login = ({ onLogin, config }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isInitialized, setIsInitialized] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [schema, setSchema] = useState(null);
  const [initError, setInitError] = useState(null);

  // Password validation states
  const [hasMin, setHasMin] = useState(false);
  const [hasUpper, setHasUpper] = useState(false);
  const [hasLower, setHasLower] = useState(false);
  const [hasDigits, setHasDigits] = useState(false);
  const [hasSymbols, setHasSymbols] = useState(false);

  // Email/password-reset feature. Hidden entirely unless the deployment reports
  // emailEnabled (GET /api/config/deployment).
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotSubmitting, setForgotSubmitting] = useState(false);

  useEffect(() => {
    setSchema(buildPasswordSchema());
  }, []);

  useEffect(() => {
    if (schema) {
      const reqs = checkPasswordRequirements(schema, password);
      setHasMin(reqs.hasMin);
      setHasUpper(reqs.hasUpper);
      setHasLower(reqs.hasLower);
      setHasDigits(reqs.hasDigits);
      setHasSymbols(reqs.hasSymbols);
    }
  }, [password]);

  useEffect(() => {
    let cancelled = false;
    const loadDeploymentConfig = async () => {
      // The `config` prop is the static app config (/config). Reuse it if it already
      // carries the deployment flag; otherwise ask the platform API directly
      // (unauthenticated endpoint, same client as /users/login).
      if (config && typeof config.emailEnabled === 'boolean') {
        setEmailEnabled(config.emailEnabled);
        return;
      }
      try {
        const deployment = await API.get('api', '/api/config/deployment');
        if (cancelled) return;
        setEmailEnabled(deployment?.emailEnabled === true);
      } catch (err) {
        // Feature stays hidden when the flag can't be read — existing behavior preserved.
        console.log('deployment config fetch failed; email features hidden', err);
        if (!cancelled) setEmailEnabled(false);
      }
    };
    loadDeploymentConfig();
    return () => { cancelled = true; };
  }, []);

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotSubmitting(true);
    try {
      const response = await API.post('api', '/users/forgot-password', { email: forgotEmail });
      setForgotMessage(response?.message || NEUTRAL_RESET_MESSAGE);
    } catch (err) {
      // Same neutral text on any failure — no user enumeration hints.
      setForgotMessage(NEUTRAL_RESET_MESSAGE);
    } finally {
      setForgotSubmitting(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const checkInitialization = async () => {
      try {
        const response = await API.get('api', '/users/initialize');
        if (cancelled) return;
        setIsInitialized(response.initialized);
        setInitError(null);
      } catch (err) {
        if (cancelled) return;
        console.error('Error checking initialization:', err);
        setInitError(err);
      }
    };
    checkInitialization();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      let response;
      if (!isInitialized) {
        // Creating the first admin: both password fields must match.
        if (password !== confirmPassword) {
          setError('Passwords do not match.');
          setIsLoading(false);
          return;
        }
        // Handle initialization
        response = await API.post('api','/users/initialize', { email, password });

      } else {
        // Handle normal login
        response = await API.post('api','/users/login', { email, password },true);
        console.log('LOGIN RESPONSE', response)
      }


      // {"error":"Invalid email or password","details":"Bad credentials"}
      console.log('LOGIN INIT  RESPONSE', response)
      
      // Handle both response formats:
      // 1. When full_response=true: { status: 200, data: { id, email, role, token } }
      // 2. When full_response=false: { id, email, role, token }
      let user = null;
      if (response && response.status === 200 && response.data && response.data.token) {
        // Normal login format (full_response=true)
        user = response.data;
      } else if (response && response.token) {
        // Initialization format (full_response=false/undefined)
        user = response;
      }

      if (user && user.token) {
        // RestAuthManager.setUserData(response);
        // RestAuthManager.setJwtToken(response.token);
        // Set expiration to one day.            
        // console.log(user)
        // console.log('setting cookie: ' + user.token);
        const decoded = parseJwt(user.token);
        const exp = decoded.exp * 1000;

        Cookies.set('c2-discover-api-token', user.token, {
            expires: new Date(exp)
          });
          onLogin(user);
        } else {
          setError(response?.data?.error || response?.error || 'Login failed');
        }
        // setAuthToken(response.token);
      // }
      // {
      //   "error": "Invalid email or password",
      //     "details": "Bad credentials"
      // }
    } catch (err) {
      setError(err.message || 'An error occurred during authentication');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='sign-in  mh-100 mw-100' data-testid="login-page">
      {initError && <NetworkErrorOverlay error={initError} onRetry={() => window.location.reload()} />}
      <Container>
        <Row className="vh-100">
          <Col>
            <Image className='login-logo' src='/c2_platform_white.png' />
          </Col>
          <Col className={`auth-container ${''} my-auto bg-light rounded p-4` }>
            <div >
              <Form onSubmit={handleSubmit}>
                <Form.Group>
                  <Form.Label className="signin-title">{!isInitialized ? 'No Existing Users Found.' : 'Sign in to your account'}</Form.Label>
                </Form.Group>
                {
                  !isInitialized && (
                    <Form.Group>
                      <Form.Label className="signin-title">This first login will create an admin user. All 
                        other users will need to be created from the Admin panel.</Form.Label>
                    </Form.Group>
                  )

                }
                {error && <Alert variant="danger">{error}</Alert>}
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password"
                  />
                  {password && (
                    <PasswordRequirementsBox
                      passwordInput={password}
                      confirmInput={!isInitialized ? confirmPassword : undefined}
                      hasMin={hasMin}
                      hasUpper={hasUpper}
                      hasLower={hasLower}
                      hasDigits={hasDigits}
                      hasSymbols={hasSymbols}
                    />
                  )}
                </Form.Group>
                {!isInitialized && (
                  <Form.Group className="mb-3">
                    <Form.Label>Confirm Password</Form.Label>
                    <Form.Control
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </Form.Group>
                )}
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isLoading || (!isInitialized && (password === '' || password !== confirmPassword))}
                  className="w-100"
                  data-testid="login-submit"
                >
                  {isLoading ? 'Loading...' : isInitialized ? 'Sign In' : 'Create Admin User'}
                </Button>
              </Form>
              {isInitialized && emailEnabled && (
                <div className='mt-3'>
                  <Button
                    variant='link'
                    className='p-0'
                    data-testid='forgot-password-link'
                    onClick={() => {
                      setForgotMessage('');
                      if (!showForgot && forgotEmail === '' && email !== '') {
                        setForgotEmail(email);
                      }
                      setShowForgot(!showForgot);
                    }}
                  >
                    Forgot password?
                  </Button>
                  {showForgot && (
                    forgotMessage ? (
                      <Alert variant='secondary' className='mt-2 mb-0' data-testid='forgot-password-message'>
                        {forgotMessage}
                      </Alert>
                    ) : (
                      <Form onSubmit={handleForgotSubmit} className='mt-2'>
                        <Form.Group className='mb-2'>
                          <Form.Label>Email</Form.Label>
                          <Form.Control
                            type='email'
                            value={forgotEmail}
                            onChange={(e) => setForgotEmail(e.target.value)}
                            required
                            data-testid='forgot-password-email'
                          />
                        </Form.Group>
                        <Button
                          variant='outline-primary'
                          type='submit'
                          disabled={forgotSubmitting || forgotEmail === ''}
                          data-testid='forgot-password-submit'
                        >
                          {forgotSubmitting ? 'Sending...' : 'Send reset link'}
                        </Button>
                      </Form>
                    )
                  )}
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const NetworkErrorOverlay = ({ error, onRetry }) => {
  const status = error && error.status;
  const statusLine = status
    ? `HTTP ${status}${error.statusText ? ' ' + error.statusText : ''}`
    : 'Network error';
  const target = error && (error.method || error.url)
    ? `${error.method || ''} ${error.url || ''}`.trim()
    : null;
  const bodyText = error && error.body
    ? (typeof error.body === 'string' ? error.body : JSON.stringify(error.body, null, 2))
    : null;

  return (
    <Modal show={true} centered backdrop="static" keyboard={false}>
      <Modal.Header>
        <Modal.Title>Unable to reach platform API</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          The UI cannot reach the platform services. Please check the cluster
          configuration (load balancer, ingress, and API container health) and
          try again.
        </p>
        <Alert variant="danger" style={{ marginBottom: 0 }}>
          <div><strong>{statusLine}</strong></div>
          {target && (
            <div style={{ fontFamily: 'monospace', fontSize: '0.85em', wordBreak: 'break-all' }}>
              {target}
            </div>
          )}
          {error && error.message && (
            <div style={{ marginTop: '0.5em', fontSize: '0.9em' }}>{error.message}</div>
          )}
          {bodyText && (
            <pre style={{ marginTop: '0.5em', marginBottom: 0, fontSize: '0.8em', whiteSpace: 'pre-wrap' }}>
              {bodyText}
            </pre>
          )}
        </Alert>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onRetry}>Retry</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default Login;